import { Directive, ElementRef, inject, Input, NgZone, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { baseStatusStyles } from './shared/statusUIIcons';

@Directive({
  selector: '[appChips]'
})
export class ChipsDirective {
  private prevStatusClass: string | null = null;
  private _status = '';
  private _fontSize = '';
  private _iconSet: 'material-icons' | 'material-symbols-outlined' = 'material-icons';
  private _showText: boolean = true;
  private renderQueued = false;
  private lastRenderedState = '';
  // Single input: status text (external alias is appChips)
  @Input('appChips')
  set status(value: string | null | undefined) {
    const normalized = (value ?? '').toString();
    if (this._status === normalized) return;
    this._status = normalized;
    this.scheduleRender();
  }

  // second input
  @Input()
  set fontSize(value: string | null | undefined) {
    const normalized = (value ?? '').toString();
    if (this._fontSize === normalized) return;
    this._fontSize = normalized;
    this.scheduleRender();
  }

  // third input (optional)
  @Input()
  set iconSet(value: 'material-icons' | 'material-symbols-outlined' | null | undefined) {
    if (!value || this._iconSet === value) return;
    this._iconSet = value;
    this.scheduleRender();
  }

  @Input()
  set showText(value: boolean | null | undefined) {
    const normalized = value ?? true;
    if (this._showText === normalized) return;
    this._showText = normalized;
    this.scheduleRender();
  }

  constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2, private ngZone: NgZone) { }

  private scheduleRender(): void {
    if (this.renderQueued) return;
    this.renderQueued = true;
    // Batch renders to the next animation frame and perform DOM work outside Angular
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.renderQueued = false;
        // keep actual DOM mutations outside Angular change-detection
        this.render();
      });
    });
  }

  private render() {
    const host = this.el.nativeElement;
    const normalized = this._status.trim().toLowerCase();
    const renderState = `${normalized}|${this._fontSize}|${this._iconSet}|${this._showText}`;

    if (this.lastRenderedState === renderState) {
      return;
    }
    this.lastRenderedState = renderState;

    // Base classes
    this.renderer.addClass(host, 'small-chips');
    this.renderer.addClass(host, 'g-just-flex');
    this.renderer.addClass(host, 'gap-8');
    this.renderer.addClass(host, 'py-8');

    // Apply optional font size to host
    if (this._fontSize) {
      this.renderer.setStyle(host, 'font-size', this._fontSize, RendererStyleFlags2.Important);
      this.renderer.setStyle(host, 'line-height', Number(this._fontSize.match(/\d+/)?.[0] || 16) +
        Number(Number(this._fontSize.match(/\d+/)?.[0]) / 2) +
        'px' || 'normal', RendererStyleFlags2.Important);
    }

    host.style.setProperty('margin', '0', 'important');
    // Also via Renderer2 for cross-renderer compatibility
    this.renderer.setStyle(host, 'margin', '0', RendererStyleFlags2.Important);
    this.renderer.setAttribute(host, 'title', this._status || '');

    // Dynamic status-* class
    const nextStatusClass = `status-${baseStatusStyles[normalized]?.color || 'gray'}`;
    if (this.prevStatusClass && this.prevStatusClass !== nextStatusClass) {
      this.renderer.removeClass(host, this.prevStatusClass);
    }
    this.renderer.addClass(host, nextStatusClass);
    this.prevStatusClass = nextStatusClass;

    // Clear current content quickly
    try {
      this.renderer.setProperty(host, 'innerHTML', '');
    } catch (e) {
      // fallback to safe removal if renderer doesn't support setProperty in this renderer
      while (host.firstChild) {
        this.renderer.removeChild(host, host.firstChild);
      }
    }
    if (this._showText) {
      this.renderer.removeClass(host, 'no-border');
      // <span>{{ status }}</span>
      const span = this.renderer.createElement('span');

      this.renderer.appendChild(span, this.renderer.createText(this._status || ''));

      this.renderer.appendChild(host, span);
    } else {
      this.renderer.addClass(host, 'no-border');
    }
    // Render icon using ligature font (no MatIconComponent needed)
    const iconName = baseStatusStyles[normalized]?.icon || 'help_outline';
    const iconSpan = this.renderer.createElement('span');
    this.renderer.addClass(iconSpan, this._iconSet); // 'material-icons' or 'material-symbols-outlined'
    if (this._fontSize) {
      this.renderer.setStyle(iconSpan, 'font-size', this._fontSize);
      this.renderer.setStyle(iconSpan, 'height', this._fontSize);
      this.renderer.setStyle(iconSpan, 'width', this._fontSize);
    }
    this.renderer.setStyle(iconSpan, 'backgroundColor', 'transparent');
    this.renderer.setStyle(iconSpan, 'borderColor', 'transparent');
    this.renderer.appendChild(iconSpan, this.renderer.createText(iconName));
    this.renderer.appendChild(host, iconSpan);
  }
}
